/**
 * Card style configuration utilities for ProductCard.
 * Pure functions — no component state, no hooks.
 */

const CATEGORY_COLORS = {
  beverages: '#3B82F6',
  snacks: '#F59E0B',
  candy: '#EC4899',
  tobacco: '#8B5CF6',
  default: '#10B981',
};

const CATEGORY_GRADIENTS = {
  beverages: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  snacks: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  candy: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  tobacco: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  default: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
};

export function getCategoryColor(category) {
  const key = (category || '').toLowerCase();
  return CATEGORY_COLORS[key] || CATEGORY_COLORS.default;
}

export function getCategoryGradient(category) {
  const key = (category || '').toLowerCase();
  return CATEGORY_GRADIENTS[key] || CATEGORY_GRADIENTS.default;
}

/**
 * Returns the style configuration object for a product card button.
 *
 * @param {object} opts
 * @param {string}  opts.cardStyle           - selected style name
 * @param {boolean} opts.isPressed           - interaction state
 * @param {string}  opts.effectiveBorderColor - per-button or category color / gradient
 * @param {boolean} opts.isGradientBorder    - whether effectiveBorderColor is a gradient string
 * @param {string}  opts.buttonShadow        - pre-computed box-shadow value
 * @param {string|undefined} opts.pressedBorder - border override when pressed
 */
export function getCardStyleConfig({ cardStyle, isPressed, effectiveBorderColor, isGradientBorder, buttonShadow, pressedBorder }) {
  switch (cardStyle) {
    case 'gradient-border': {
      const gradientValue = isGradientBorder
        ? effectiveBorderColor
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #667eea 100%)';
      return {
        className: 'product-card-gradient',
        style: {
          background: `linear-gradient(white, white) padding-box, ${gradientValue} border-box`,
          border: pressedBorder || '3px solid transparent',
          boxShadow: buttonShadow,
          backgroundSize: '300% 300%',
          animation: 'gradient-shift 8s ease infinite',
          borderRadius: '24px',
        },
        textColor: 'text-zinc-900',
      };
    }

    case 'top-accent':
      return {
        className: 'product-card-top-accent',
        style: {
          background: isPressed ? `${effectiveBorderColor}33` : '#ffffff',
          borderTop: `5px solid ${effectiveBorderColor}`,
          borderBottom: `1px solid ${effectiveBorderColor}`,
          borderLeft: `1px solid ${effectiveBorderColor}`,
          borderRight: `1px solid ${effectiveBorderColor}`,
          boxShadow: isPressed ? 'inset 0 2px 8px rgba(0,0,0,0.1)' : buttonShadow,
          transition: 'all 0.15s ease-in-out',
        },
        textColor: 'text-zinc-900',
      };

    case 'left-accent':
      return {
        className: 'product-card-left-accent',
        style: {
          background: isPressed ? `${effectiveBorderColor}33` : '#ffffff',
          borderLeft: `5px solid ${effectiveBorderColor}`,
          borderTop: `1px solid ${effectiveBorderColor}`,
          borderBottom: `1px solid ${effectiveBorderColor}`,
          borderRight: `1px solid ${effectiveBorderColor}`,
          boxShadow: isPressed ? 'inset 2px 0 8px rgba(0,0,0,0.1)' : buttonShadow,
          transition: 'all 0.15s ease-in-out',
        },
        textColor: 'text-zinc-900',
      };

    case 'right-accent':
      return {
        className: 'product-card-right-accent',
        style: {
          background: isPressed ? `${effectiveBorderColor}33` : '#ffffff',
          borderRight: `5px solid ${effectiveBorderColor}`,
          borderTop: `1px solid ${effectiveBorderColor}`,
          borderBottom: `1px solid ${effectiveBorderColor}`,
          borderLeft: `1px solid ${effectiveBorderColor}`,
          boxShadow: isPressed ? 'inset -2px 0 8px rgba(0,0,0,0.1)' : buttonShadow,
          transition: 'all 0.15s ease-in-out',
        },
        textColor: 'text-zinc-900',
      };

    case 'bottom-accent':
      return {
        className: 'product-card-bottom-accent',
        style: {
          background: isPressed ? `${effectiveBorderColor}33` : '#ffffff',
          borderBottom: `5px solid ${effectiveBorderColor}`,
          borderTop: `1px solid ${effectiveBorderColor}`,
          borderLeft: `1px solid ${effectiveBorderColor}`,
          borderRight: `1px solid ${effectiveBorderColor}`,
          boxShadow: isPressed ? 'inset 0 -2px 8px rgba(0,0,0,0.1)' : buttonShadow,
          transition: 'all 0.15s ease-in-out',
        },
        textColor: 'text-zinc-900',
      };

    case 'glass':
      return {
        className: 'product-card-glass',
        style: {
          background: isPressed ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: isPressed
            ? `2px solid ${effectiveBorderColor}`
            : '1px solid rgba(255, 255, 255, 0.8)',
          boxShadow: isPressed
            ? `0 8px 32px ${effectiveBorderColor}40`
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
        },
        textColor: 'text-zinc-800',
      };

    case 'outlined':
      return {
        className: 'product-card-outlined',
        style: {
          background: isPressed ? `${effectiveBorderColor}15` : 'transparent',
          border: `2px solid ${isPressed ? effectiveBorderColor : '#d1d5db'}`,
          boxShadow: isPressed ? `0 0 0 3px ${effectiveBorderColor}30` : 'none',
          borderRadius: '10px',
          transition: 'all 0.2s ease',
        },
        textColor: isPressed ? 'text-zinc-900' : 'text-zinc-600',
      };

    case 'ios':
      return {
        className: 'product-card-ios',
        style: {
          background: '#ffffff',
          border: 'none',
          boxShadow: isPressed
            ? '0 1px 3px rgba(0, 0, 0, 0.12)'
            : '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
          borderRadius: '14px',
          transform: isPressed ? 'scale(0.98)' : 'scale(1)',
          transition: 'all 0.15s ease',
        },
        textColor: 'text-zinc-900',
      };

    case 'pill':
      return {
        className: 'product-card-pill',
        style: {
          background: isPressed
            ? `linear-gradient(180deg, ${effectiveBorderColor}15 0%, ${effectiveBorderColor}25 100%)`
            : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          border: `2px solid ${effectiveBorderColor}`,
          boxShadow: isPressed
            ? `inset 0 2px 8px ${effectiveBorderColor}30`
            : `0 4px 12px ${effectiveBorderColor}20`,
          borderRadius: '24px',
          transition: 'all 0.15s ease',
        },
        textColor: 'text-zinc-800',
      };

    default: {
      const gradientValue = isGradientBorder
        ? effectiveBorderColor
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #667eea 100%)';
      return {
        className: 'product-card-gradient',
        style: {
          background: `linear-gradient(white, white) padding-box, ${gradientValue} border-box`,
          border: pressedBorder || '3px solid transparent',
          boxShadow: buttonShadow,
          backgroundSize: '300% 300%',
          animation: 'gradient-shift 8s ease infinite',
          borderRadius: '24px',
        },
        textColor: 'text-zinc-900',
      };
    }
  }
}
